import {
  computed,
  getCurrentScope,
  inject,
  onScopeDispose,
  provide,
  readonly,
  ref,
  watch,
  type ComputedRef,
  type DeepReadonly,
  type InjectionKey,
  type Ref,
} from 'vue'

import type { ChatMessage } from '../models/orpheus.types'
import {
  isProgressPhase,
  ORPHEUS_PROGRESS_EVENT,
  parseTurnProgressFrame,
  progressLabelKey,
  type TurnProgressLine,
} from '../service/turnProgress'
import { useOrpheusStore } from '../stores/orpheusStore'

import { useI18n } from '@/shared/i18n'

export interface OrpheusProgressEventBus {
  on: (name: typeof ORPHEUS_PROGRESS_EVENT, handler: (payload: unknown) => void) => () => void
}

let installedBus: OrpheusProgressEventBus | null = null

/** Composition-root injection: feature code never imports the app shell. */
export function registerOrpheusEventBus(bus: OrpheusProgressEventBus): void {
  installedBus = bus
}

export function resetOrpheusEventBusForTest(): void {
  installedBus = null
}

/** Every line stays up this long; phases arriving meanwhile collapse into the latest one. */
export const MIN_LINE_MS = 1000
/** With nothing past `started` by then, the line turns into a seconds counter. */
export const FALLBACK_AFTER_SECONDS = 5
const TICK_MS = 1000

export interface TurnProgress {
  /** The turn the line follows, or null when no answer is in flight. */
  turnId: DeepReadonly<Ref<string | null>>
  line: DeepReadonly<Ref<TurnProgressLine | null>>
  elapsedSeconds: DeepReadonly<Ref<number>>
}

export interface UseOrpheusTurnProgressOptions {
  /** Defaults to the bus `app/bootstrap.ts` registered; none means no frames, only the counter. */
  bus?: OrpheusProgressEventBus | null
  now?: () => number
}

/**
 * Tracks the progress line of the turn `waitingTurnId` returns (v3 `useOrpheusTurnProgress.js`).
 * Frames are matched on the turn's `client_turn_id`, which is the turn id the chat POST sends.
 * `completed`/`failed` don't change the line: the chat response closes the box and carries any
 * error.
 */
export function useOrpheusTurnProgress(
  waitingTurnId: () => string | null,
  options: UseOrpheusTurnProgressOptions = {},
): TurnProgress {
  const bus = options.bus === undefined ? installedBus : options.bus
  const now = options.now ?? Date.now
  const turnId = ref<string | null>(null)
  const line = ref<TurnProgressLine | null>(null)
  const elapsedSeconds = ref(0)

  let sentAt = 0
  let shownAt = 0
  let pendingLine: TurnProgressLine | null = null
  let switchTimer: ReturnType<typeof setTimeout> | null = null
  let tickTimer: ReturnType<typeof setInterval> | null = null

  function show(next: TurnProgressLine): void {
    line.value = next
    shownAt = now()
  }

  function flushPending(): void {
    if (!pendingLine || switchTimer) return
    if (pendingLine === line.value) {
      pendingLine = null
      return
    }
    const remaining = MIN_LINE_MS - (now() - shownAt)
    if (remaining > 0) {
      switchTimer = setTimeout(() => {
        switchTimer = null
        flushPending()
      }, remaining)
      return
    }
    show(pendingLine)
    pendingLine = null
  }

  function tick(): void {
    elapsedSeconds.value = Math.round((now() - sentAt) / 1000)
    if (
      line.value === 'started' &&
      !pendingLine &&
      elapsedSeconds.value >= FALLBACK_AFTER_SECONDS
    ) {
      show('working')
    }
  }

  function clearTimers(): void {
    if (switchTimer) clearTimeout(switchTimer)
    if (tickTimer) clearInterval(tickTimer)
    switchTimer = null
    tickTimer = null
  }

  function handleFrame(payload: unknown): void {
    const frame = parseTurnProgressFrame(payload)
    if (!frame || !turnId.value || frame.clientTurnId !== turnId.value) return
    if (frame.phase === 'started' || !isProgressPhase(frame.phase)) return
    pendingLine = frame.phase
    flushPending()
  }

  watch(
    waitingTurnId,
    (next) => {
      clearTimers()
      pendingLine = null
      turnId.value = next
      if (!next) {
        line.value = null
        elapsedSeconds.value = 0
        return
      }
      sentAt = now()
      show('started')
      tick()
      tickTimer = setInterval(tick, TICK_MS)
    },
    { immediate: true },
  )

  const unsubscribe = bus?.on(ORPHEUS_PROGRESS_EVENT, handleFrame) ?? null

  if (getCurrentScope()) {
    onScopeDispose(() => {
      clearTimers()
      unsubscribe?.()
    })
  }

  return {
    turnId: readonly(turnId),
    line: readonly(line),
    elapsedSeconds: readonly(elapsedSeconds),
  }
}

const TURN_PROGRESS_KEY: InjectionKey<TurnProgress> = Symbol('orpheusTurnProgress')

/**
 * Mounted once by the conversation, for the page's lifetime rather than per waiting answer: the
 * workspace stream opens only while it has a subscriber, and a stream opened at send time would
 * miss the turn's first frames.
 */
export function provideOrpheusTurnProgress(
  options: UseOrpheusTurnProgressOptions = {},
): TurnProgress {
  const store = useOrpheusStore()
  const progress = useOrpheusTurnProgress(() => {
    const message = store.activeAssistantMessage
    if (!message) return null
    const inFlight = message.streamState === 'connecting' || message.streamState === 'streaming'
    return inFlight ? message.turnId : null
  }, options)
  provide(TURN_PROGRESS_KEY, progress)
  return progress
}

export interface TurnProgressLabel {
  /** Changes only when the phase does — the counter ticking keeps the same key. */
  key: TurnProgressLine
  text: string
}

/**
 * The live line for one waiting answer; null where no conversation provides one (the feature chat
 * panel, which posts elsewhere) or while the line follows another turn.
 */
export function useTurnProgressLabel(
  message: () => Pick<ChatMessage, 'turnId'>,
): ComputedRef<TurnProgressLabel | null> {
  const progress = inject(TURN_PROGRESS_KEY, null)
  const { t } = useI18n()
  return computed(() => {
    if (!progress) return null
    const line = progress.line.value
    if (!line || progress.turnId.value !== message().turnId) return null
    return {
      key: line,
      text: t(progressLabelKey(line), { seconds: progress.elapsedSeconds.value }),
    }
  })
}
